import { useState, useEffect } from 'react'
import { clsx } from 'clsx'
import { X, Eye, PencilLine, QrCode, Archive, Calendar, Scale, Hash, Layout, Layers, Box } from 'lucide-react'
import { Card, Badge, Button, Input, Select, SearchBar, Divider, Modal, KVRow, SectionHeading } from '../../../shared/components/UI'
import { QRCodeDisplay, printQR } from '../../../shared/lib/qrUtils'
import { containersApi, unitsApi } from '../../../shared/lib/api'

const TYPES = ['Bobbin', 'Jari Roll']

const STATUS_BADGE = {
  available: { variant: 'green', label: 'Available' },
  in_use: { variant: 'amber', label: 'In Use' },
  empty: { variant: 'gray', label: 'Empty' },
}

const TYPE_CONFIG = {
  'Bobbin': { bg: 'bg-brand-50', text: 'text-brand-700', border: 'border-brand-200' },
  'Jari Roll': { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
}

const emptyForm = { containerId: '', type: 'Bobbin', unitId: '', tareWeight: '', capacity: 75 }

export default function ContainersPage() {
  const [containers, setContainers] = useState([])
  const [units, setUnits] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('All')
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [showQrFor, setShowQrFor] = useState(null)
  const [viewing, setViewing] = useState(null)

  const vBatches = viewing ? (viewing.batches || []).filter(b => ['created', 'closed'].includes(b.status)) : []
  const vTare = viewing ? parseFloat(viewing.tare_weight_kg || 0) : 0
  
  // vNet: Pure Yarn Weight only (Sum of total_weight_kg from batches)
  const vNet = vBatches.reduce((sum, b) => sum + (parseFloat(b.total_weight_kg) || 0), 0)
  
  // vTotalBobbin: Sum of bobbin tares (with 80g fallback)
  const vTotalBobbin = vBatches.reduce((sum, b) => {
    const bWeight = parseFloat(b.bobbin_weight_kg) > 0 ? parseFloat(b.bobbin_weight_kg) : (b.bobbin_count * 0.080);
    return sum + bWeight;
  }, 0)

  // vGross: Correct Calculated Gross (Tare + Yarn + Bobbins)
  const vGross = vBatches.length > 0 ? (vTare + vNet + vTotalBobbin) : vTare

  useEffect(() => {
    Promise.all([
      containersApi.list(),
      unitsApi.list()
    ]).then(([cntRes, unitRes]) => {
      setContainers(cntRes.data.data)
      setUnits(unitRes.data.data)
    }).catch((err) => {
      console.error('Failed to load container data:', err)
    }).finally(() => setLoading(false))
  }, [])

  const f = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }))

  const visible = containers.filter((c) => {
    const matchSearch = (c.container_id || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.unit?.name || '').toLowerCase().includes(search.toLowerCase())
    const matchType = typeFilter === 'All' || c.type === typeFilter
    return matchSearch && matchType
  })

  const startNew = () => {
    setViewing(null)
    setShowQrFor(null)
    setEditing('new')
    setForm({ ...emptyForm, unitId: units[0]?.id || '' })
  }

  const startEdit = (c) => {
    setViewing(null)
    setShowQrFor(null)
    setEditing(c.id)
    setForm({
      containerId: c.container_id,
      type: c.type,
      unitId: c.unit_id,
      tareWeight: c.tare_weight_kg,
      capacity: c.capacity || 75
    })
  }

  const cancel = () => { setEditing(null); setForm(emptyForm) }

  const save = () => {
    if (!form.containerId.trim() || !form.tareWeight || !form.unitId) return
    const payload = {
      containerId: form.containerId.trim(),
      type: form.type,
      unitId: form.unitId,
      tareWeightKg: parseFloat(form.tareWeight),
      capacity: parseInt(form.capacity || 75),
    }

    const request = editing === 'new'
      ? containersApi.create(payload)
      : containersApi.update(editing, payload)

    request.then(() => {
      loadContainers()
      cancel()
    }).catch(console.error)
  }

  const loadContainers = () => {
    setLoading(true)
    containersApi.list().then((res) => {
      setContainers(res.data.data)
      setLoading(false)
    })
  }


  const inUseCount = containers.filter((c) => c.status === 'in_use').length
  const panelContainer = containers.find((x) => x.id === showQrFor)

  if (loading) return <div className="p-8 text-center text-gray-400">Loading containers...</div>

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-t-4 border-gray-100 border-t-brand-600 p-4">
          <p className="stat-label">Total Containers</p>
          <p className="stat-val">{containers.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-t-4 border-gray-100 border-t-amber-500 p-4">
          <p className="stat-label">In Use</p>
          <p className="stat-val">{inUseCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-t-4 border-gray-100 border-t-green-500 p-4">
          <p className="stat-label">Available</p>
          <p className="stat-val">{containers.filter((c) => c.status !== 'in_use').length}</p>
        </div>
      </div>

      <div className="space-y-4">
        <SearchBar value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by container ID or unit...">
          <Button onClick={startNew}>+ Add Container</Button>
        </SearchBar>

        <div className="flex gap-2">
              {['All', 'Bobbin', 'Jari Roll'].map((t) => (
                <button key={t} onClick={() => setTypeFilter(t)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${typeFilter === t
                    ? 'bg-brand-600 text-white border-brand-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}>
                {t}
              </button>
            ))}
          </div>

        {/* Table */}
        <Card bodyClass="!p-0 overflow-hidden ring-1 ring-gray-100/50 shadow-sm rounded-2xl">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead className="bg-gray-50/50">
                <tr>
                  <th>Container ID</th>
                  <th>Type</th>
                  <th>Unit</th>
                  <th>Shade</th>
                  <th>Capacity</th>
                  <th>Items</th>
                  <th>Source</th>
                  <th>Weight</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {visible.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-20 text-gray-400 text-sm">No containers found</td></tr>
                )}
                {visible.map((c) => {
                  const s = STATUS_BADGE[c.status] || STATUS_BADGE.available
                  const tc = TYPE_CONFIG[c.type] || TYPE_CONFIG['Bobbin']
                  const activeBatches = (c.batches || []).filter(b => ['created', 'closed'].includes(b.status))
                  const tare = parseFloat(c.tare_weight_kg || 0)
                  const current = parseFloat(c.current_weight_kg || 0)
                  
                  // Use raw gross weight from batches (entered by MM) as primary source
                  const gross = activeBatches.length > 0
                    ? activeBatches.reduce((sum, b) => sum + parseFloat(b.gross_weight_kg || 0), 0)
                    : tare
                  return (
                    <tr key={c.id} className="group hover:bg-brand-50/20 transition-colors">
                      <td className="font-black text-brand-600 font-mono text-sm py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-brand-500 shadow-[0_0_8px_rgba(var(--brand-600),0.5)]" />
                          {c.container_id}
                        </div>
                      </td>
                      <td>
                        <span className={`text-[10px] font-black px-2.5 py-1 rounded-full border uppercase tracking-widest ${tc.bg} ${tc.text} ${tc.border}`}>
                          {c.type}
                        </span>
                      </td>
                      <td className="text-gray-500 text-xs font-medium">{c.unit?.name || '—'}</td>
                      <td>
                        {c.shade ? (
                          <Badge variant="blue" className="text-[9px] uppercase font-black">{c.shade.name}</Badge>
                        ) : (
                          <span className="text-gray-300 text-[10px] font-bold">—</span>
                        )}
                      </td>
                      <td className="text-gray-600 font-bold text-xs">{c.capacity || 75} pcs</td>
                      <td className="font-black text-brand-600 text-xs">
                        {activeBatches.reduce((sum, b) => sum + (b.bobbin_count || 0), 0) || 0}
                      </td>
                      <td>
                        {activeBatches.length > 0 ? (
                          <div className="flex flex-col gap-1">
                            {activeBatches.map((b, idx) => (
                              <div key={b.id || idx} className="flex flex-col">
                                <span className="text-[10px] font-black text-brand-700 leading-tight">{b.batch_number}</span>
                                <span className="text-[9px] text-gray-400 uppercase font-bold tracking-tighter">
                                  {b.roll?.roll_code || '—'}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="py-4">
                        <div className="flex flex-col">
                          {activeBatches.length > 0 ? (
                            <div className="flex flex-col">
                              <div className="flex items-baseline gap-1">
                                <span className="text-sm font-black text-gray-900">
                                  {parseFloat(c.display_gross_weight || 0).toFixed(2)}
                                </span>
                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter">KG Gross</span>
                              </div>
                              <div className="flex items-center gap-1.5 -mt-0.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                  <div className="w-0.5 h-0.5 rounded-full bg-emerald-500" />
                                </div>
                                <span className="text-[10px] text-emerald-600 font-black uppercase tracking-tight">
                                  Net Yarn: {parseFloat(c.net_yarn_weight_kg || 0).toFixed(2)} KG
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-baseline gap-1.5">
                              <span className="text-base font-black text-gray-300">{tare.toFixed(2)}</span>
                              <span className="text-[10px] text-gray-300 font-bold uppercase tracking-wider">KG Tare</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="flex justify-start">
                          <Badge variant={s.variant} className="uppercase text-[9px] tracking-widest font-black">{s.label}</Badge>
                        </div>
                      </td>
                      <td className="pr-4">
                        <div className="flex gap-1 justify-start items-center">
                          <button 
                            onClick={() => {
                              setEditing(null)
                              setShowQrFor(null)
                              setViewing(c)
                            }}
                            className="p-2 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-xl transition-all"
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>
                          <button 
                            onClick={() => startEdit(c)}
                            className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-xl transition-all"
                            title="Edit"
                          >
                            <PencilLine size={16} />
                          </button>
                          <button 
                            onClick={() => {
                              setEditing(null)
                              setViewing(null)
                              setShowQrFor(showQrFor === c.id ? null : c.id)
                            }}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                            title="Print QR"
                          >
                            <QrCode size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

        {/* View Modal */}
        {viewing && (
          <Modal
            isOpen={!!viewing}
            onClose={() => setViewing(null)}
            title={`Container Details • ${viewing.container_id}`}
            size="md"
          >
            <div className="space-y-6">
              {/* Hero Section */}
              <div className="p-6 bg-brand-50/50 rounded-2xl border border-brand-100/50 flex flex-col items-center gap-3">
                <div className="w-14 h-14 bg-white rounded-2xl shadow-sm border border-brand-100 flex items-center justify-center text-brand-600">
                  <Archive size={28} />
                </div>
                <div className="text-center">
                  <h3 className="text-2xl font-black text-gray-900 tracking-tight font-mono leading-none">{viewing.container_id}</h3>
                  <div className="flex gap-2 justify-center mt-2">
                    <Badge variant={STATUS_BADGE[viewing.status]?.variant || 'gray'}>{STATUS_BADGE[viewing.status]?.label}</Badge>
                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest self-center">{viewing.type}</span>
                  </div>
                </div>
              </div>

                {/* Core Stats */}
                <div className="grid grid-cols-2 gap-3 px-2">
                  {vBatches.length > 0 ? (
                    <>
                      <div className="p-3 bg-white border border-emerald-100 rounded-2xl shadow-sm text-center">
                        <span className="text-[9px] font-black uppercase tracking-widest text-emerald-400 block mb-1">Gross Weight</span>
                        <p className="text-sm font-black text-emerald-600">{vGross.toFixed(2)} KG</p>
                      </div>
                      <div className="p-3 bg-white border border-brand-100 rounded-2xl shadow-sm text-center">
                        <span className="text-[9px] font-black uppercase tracking-widest text-brand-400 block mb-1">Net Yarn Content</span>
                        <p className="text-sm font-black text-brand-600">{vNet.toFixed(2)} KG</p>
                      </div>
                    </>
                  ) : (
                    <div className="col-span-2 p-3 bg-white border border-gray-100 rounded-2xl shadow-sm text-center">
                      <span className="text-[9px] font-black uppercase tracking-widest text-gray-400 block mb-1">Container Weight</span>
                      <p className="text-sm font-black text-gray-800">{vTare.toFixed(2)} KG</p>
                    </div>
                  )}
                </div>

              <div className="grid grid-cols-2 gap-4 px-2">
                <div className="p-4 bg-white border border-gray-100 rounded-2xl shadow-sm">
                  <div className="flex items-center gap-2 mb-1 text-gray-400">
                    <Layout size={14} />
                    <span className="text-[10px] font-black uppercase tracking-widest">Capacity</span>
                  </div>
                  <p className="text-base font-black text-gray-800">{viewing.capacity || 75} <span className="text-[10px] text-gray-400 font-medium">PCS</span></p>
                </div>
                <div className="p-4 bg-white border border-gray-100 rounded-2xl shadow-sm">
                  <div className="flex items-center gap-2 mb-1 text-gray-400">
                    <Layers size={14} />
                    <span className="text-[10px] font-black uppercase tracking-widest">Filled</span>
                  </div>
                  <p className="text-base font-black text-brand-600">
                    {vBatches.reduce((sum, b) => sum + (b.bobbin_count || 0), 0)} <span className="text-[10px] text-gray-400 font-medium">ITEMS</span>
                  </p>
                </div>
              </div>

              {/* Content Breakdown */}
              {viewing.status === 'in_use' && viewing.batches && (viewing.batches.filter(b => ['created', 'closed'].includes(b.status))).length > 0 && (
                <div className="space-y-4 px-2">
                  <div className="flex justify-between items-end">
                    <SectionHeading>Active Content (Mixed Batches)</SectionHeading>
                    <span className="text-[10px] text-gray-400 font-bold uppercase mb-1">{(viewing.batches.filter(b => ['created', 'closed'].includes(b.status))).length} Batch(es)</span>
                  </div>
                  
                  <div className="space-y-3">
                    {viewing.batches.filter(b => ['created', 'closed'].includes(b.status)).map((b, idx) => {
                      // Fallback logic: If bobbin_weight_kg is missing, use 80g (0.08kg) per bobbin
                      const bobbinWeight = parseFloat(b.bobbin_weight_kg) > 0 
                        ? parseFloat(b.bobbin_weight_kg) 
                        : (b.bobbin_count * 0.080);
                      
                      return (
                        <div key={b.id || idx} className="p-4 bg-white border border-gray-100 rounded-2xl shadow-sm hover:border-brand-200 transition-all group">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-xs font-black text-brand-600 tracking-tight font-mono">{b.batch_number}</p>
                                {b.parent_batch_id && <Badge variant="gray" className="text-[8px] py-0">Split</Badge>}
                              </div>
                              <p className="text-[10px] font-bold text-gray-400 uppercase mt-0.5 tracking-wider">{b.shade?.name || 'Unknown Shade'}</p>
                            </div>
                            <div className="text-right">
                               <p className="text-xs font-black text-gray-900">{b.bobbin_count} <span className="text-[9px] text-gray-400 font-bold">Bobbins</span></p>
                               <p className="text-[9px] text-gray-400 font-bold uppercase tracking-tighter">Roll: {b.roll?.roll_code || '—'}</p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-50 group-hover:border-brand-50 transition-colors">
                              <div>
                                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1">Net Yarn</p>
                                <p className="text-xs font-black text-emerald-600">{(parseFloat(b.total_weight_kg) || 0).toFixed(3)} KG</p>
                              </div>
                              <div className="text-right">
                                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1">Bobbin Tare</p>
                                <p className="text-xs font-black text-gray-800">{bobbinWeight.toFixed(3)} KG</p>
                              </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {/* Calculation Logic Info */}
                  <div className="mt-4 p-4 bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                      <Scale size={10} /> Gross Weight Calculation
                    </p>
                    <div className="space-y-1">
                      <div className="flex justify-between text-[11px]">
                        <span className="text-gray-500 font-medium">Container Tare:</span>
                        <span className="text-gray-700 font-bold">+{vTare.toFixed(3)} KG</span>
                      </div>
                      <div className="flex justify-between text-[11px]">
                        <span className="text-gray-500 font-medium">Total Bobbins Weight:</span>
                        <span className="text-gray-700 font-bold">
                          +{vTotalBobbin.toFixed(3)} KG
                        </span>
                      </div>
                      <div className="flex justify-between text-[11px]">
                        <span className="text-gray-500 font-medium">Total Net Yarn:</span>
                        <span className="text-emerald-600 font-bold">+{vNet.toFixed(3)} KG</span>
                      </div>
                      <div className="pt-1 mt-1 border-t border-gray-200 flex justify-between text-xs">
                        <span className="text-gray-900 font-black">Final Gross:</span>
                        <span className="text-gray-900 font-black">
                          {vGross.toFixed(3)} KG
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Meta */}
              <div className="pt-2 px-2">
                <KVRow label="Unit Assignment" value={viewing.unit?.name || '—'} />
                <KVRow label="Active Shade" value={viewing.shade?.name || 'Empty'} />
                <KVRow label="Created At" value={new Date(viewing.created_at).toLocaleDateString()} />
              </div>

              <div className="flex gap-3 mt-4">
                <Button variant="secondary" className="flex-1 !rounded-2xl py-3 font-bold" onClick={() => { setViewing(null); startEdit(viewing); }}>Edit Details</Button>
                <Button variant="secondary" className="flex-1 !rounded-2xl py-3 font-bold" onClick={() => { setViewing(null); setShowQrFor(viewing.id); }}>View QR</Button>
              </div>
            </div>
          </Modal>
        )}

        {/* QR Modal */}
        {showQrFor && panelContainer && (
          <Modal
            isOpen={!!showQrFor}
            onClose={() => setShowQrFor(null)}
            title={`Container QR • ${panelContainer.container_id}`}
            size="sm"
          >
            <div className="flex flex-col items-center gap-6 py-4">
              <QRCodeDisplay
                value={panelContainer.qr_code || panelContainer.container_id}
                label={panelContainer.container_id}
                sublabel={`${panelContainer.type} • ${panelContainer.unit?.name} • Tare: ${panelContainer.tare_weight_kg} KG`}
                size={180}
              />
              <div className="w-full text-center space-y-4">
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                  <p className="text-sm font-bold text-gray-800">{panelContainer.container_id}</p>
                  <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest">{panelContainer.type} · {panelContainer.unit?.name}</p>
                </div>
                <div className="flex gap-2">
                  <Button className="flex-1 rounded-2xl py-3 font-black" onClick={() => printQR(panelContainer.qr_code || panelContainer.container_id, panelContainer.container_id)}>Print QR Code</Button>
                  <Button variant="secondary" className="px-6 rounded-2xl" onClick={() => setShowQrFor(null)}>Close</Button>
                </div>
              </div>
            </div>
          </Modal>
        )}

        {/* Edit/Add Modal */}
        {editing && (
          <Modal
            isOpen={!!editing}
            onClose={cancel}
            title={editing === 'new' ? 'Add New Container' : `Edit ${form.containerId}`}
            size="md"
            confirmClose={form.containerId.trim() !== ''}
          >
            <div className="space-y-6">
              <Input
                label="Container ID / Code"
                value={form.containerId}
                onChange={f('containerId')}
                placeholder="e.g. C-001"
                className="rounded-xl font-bold"
              />

              <div className="space-y-3">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest pl-1">Storage Type</p>
                <div className="grid grid-cols-2 gap-3">
                  {['Bobbin', 'Jari Roll'].map((t) => {
                    const isSel = form.type === t
                    return (
                      <button
                        key={t}
                        onClick={() => setForm((p) => ({ ...p, type: t }))}
                        className={clsx(
                          'p-5 rounded-[22px] border-2 transition-all flex flex-col items-center gap-3',
                          isSel 
                            ? 'bg-brand-50/50 border-brand-500 ring-4 ring-brand-500/10' 
                            : 'bg-white border-gray-100 hover:border-gray-200'
                        )}
                      >
                        {t === 'Bobbin' ? (
                          <div className="flex flex-col items-center gap-2">
                            <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center">
                              <Box size={20} className={form.type === t ? 'text-brand-600' : 'text-gray-300'} />
                            </div>
                            <span className="uppercase tracking-widest text-[10px]">Bobbin Container</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2">
                            <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center">
                              <Layers size={20} className={form.type === t ? 'text-teal-600' : 'text-gray-300'} />
                            </div>
                            <span className="uppercase tracking-widest text-[10px]">Jari Roll Box</span>
                          </div>
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Select
                  label="Unit Assignment"
                  value={form.unitId}
                  onChange={f('unitId')}
                  options={units.map((u) => ({ label: u.name, value: u.id }))}
                  className="rounded-xl"
                />
                <Input
                  label="Capacity"
                  value={form.capacity}
                  onChange={f('capacity')}
                  type="number"
                  placeholder="75"
                  className="rounded-xl"
                />
              </div>

              <Input
                label="Tare weight (KG)"
                value={form.tareWeight}
                onChange={f('tareWeight')}
                type="number"
                step="0.1"
                placeholder="e.g. 2.5"
                hint="Weight of the empty container itself"
                className="rounded-xl"
              />

              <div className="flex gap-3 pt-2">
                <Button onClick={save} disabled={!form.containerId.trim() || !form.tareWeight} className="flex-1 py-4 rounded-2xl font-black shadow-lg shadow-brand-100">
                  {editing === 'new' ? 'Create Container' : 'Update Details'}
                </Button>
                <Button variant="secondary" onClick={cancel} className="flex-1 py-4 rounded-2xl border-gray-100 text-gray-500">Cancel</Button>
              </div>
            </div>
          </Modal>
        )}
    </div>
  )
}
